/**
 * Gobodo Scheduled Job Service
 * CRUD for ScheduledJob__c. Full definition in DefinitionJson__c; scalars for list.
 */

import type { ScheduledJob } from '../../types/scheduledJobs';
import {
  getGobodoRecords,
  getGobodoRecordById,
  createGobodoRecord,
  updateGobodoRecord,
  deleteGobodoRecord
} from './gobodoRecordService';
import { ensureObjectTypeExists } from '../../utils/gobodoObjectTypeVerifier';
import { getCustomSuffix } from '../../config/gobodoConfig';

const getObjectType = () => `ScheduledJob${getCustomSuffix()}`;

const FIELD = {
  NAME: 'Name__c',
  DESCRIPTION: 'Description__c',
  TYPE: 'Type__c',
  STATUS: 'Status__c',
  IS_ACTIVE: 'IsActive__c',
  DEFINITION_JSON: 'DefinitionJson__c',
  CREATED_DATE: 'CreatedDate__c',
  LAST_MODIFIED: 'LastModified__c'
} as const;

export async function ensureScheduledJobObjectExists(): Promise<void> {
  await ensureObjectTypeExists(getObjectType(), {
    label: 'Scheduled Job',
    pluralLabel: 'Scheduled Jobs',
    description: 'Cron expression, target action, last run, status.',
    icon: 'Clock',
    category: 'custom',
    isCustom: true,
    isActive: true
  });
}

function jobToRecord(j: Partial<ScheduledJob>): Record<string, unknown> {
  const record: Record<string, unknown> = {
    [FIELD.NAME]: j.name ?? '',
    [FIELD.DESCRIPTION]: j.description ?? '',
    [FIELD.TYPE]: j.type ?? 'custom',
    [FIELD.STATUS]: j.status ?? 'scheduled',
    [FIELD.IS_ACTIVE]: j.isActive ?? true
  };
  if (j.createdDate) record[FIELD.CREATED_DATE] = j.createdDate;
  if (j.lastModifiedDate) record[FIELD.LAST_MODIFIED] = j.lastModifiedDate;
  record[FIELD.DEFINITION_JSON] = JSON.stringify({
    schedule: j.schedule,
    configuration: j.configuration,
    notifications: j.notifications,
    retryPolicy: j.retryPolicy,
    lastRun: j.lastRun,
    nextRun: j.nextRun,
    createdBy: j.createdBy,
    lastModifiedBy: j.lastModifiedBy
  });
  return record;
}

function recordToJob(record: Record<string, any>): ScheduledJob {
  const id = record.id != null ? String(record.id) : '';
  let def: Record<string, any> = {};
  try {
    const raw = record[FIELD.DEFINITION_JSON];
    if (raw && typeof raw === 'string') def = JSON.parse(raw);
    else if (raw && typeof raw === 'object') def = raw;
  } catch {
    // keep default
  }
  return {
    id,
    name: record[FIELD.NAME] ?? '',
    description: record[FIELD.DESCRIPTION] ?? '',
    type: (record[FIELD.TYPE] as ScheduledJob['type']) ?? 'custom',
    schedule: def.schedule ?? { frequency: 'daily', timezone: 'America/New_York' },
    configuration: def.configuration ?? {},
    status: (record[FIELD.STATUS] as ScheduledJob['status']) ?? 'scheduled',
    isActive: Boolean(record[FIELD.IS_ACTIVE]),
    notifications: def.notifications ?? { onSuccess: false, onFailure: false, recipients: [] },
    retryPolicy: def.retryPolicy ?? { enabled: false, maxAttempts: 3, retryDelayMs: 5000 },
    lastRun: def.lastRun,
    nextRun: def.nextRun,
    createdDate: record[FIELD.CREATED_DATE] ?? new Date().toISOString(),
    createdBy: def.createdBy ?? 'current_user',
    lastModifiedDate: record[FIELD.LAST_MODIFIED] ?? new Date().toISOString(),
    lastModifiedBy: def.lastModifiedBy ?? 'current_user'
  };
}

export async function getJobs(): Promise<ScheduledJob[]> {
  await ensureScheduledJobObjectExists();
  const { records } = await getGobodoRecords(getObjectType());
  return records.map((r: Record<string, any>) => recordToJob(r));
}

export async function getJobById(id: string): Promise<ScheduledJob | null> {
  const record = await getGobodoRecordById(id);
  if (!record) return null;
  return recordToJob(record);
}

export async function createJob(job: Omit<ScheduledJob, 'id'>): Promise<ScheduledJob> {
  const now = new Date().toISOString();
  const full: Partial<ScheduledJob> = {
    ...job,
    createdDate: now,
    createdBy: job.createdBy ?? 'current_user',
    lastModifiedDate: now,
    lastModifiedBy: job.lastModifiedBy ?? 'current_user'
  };
  const created = await createGobodoRecord(getObjectType(), jobToRecord(full));
  return recordToJob(created);
}

export async function updateJob(jobId: string, updates: Partial<ScheduledJob>): Promise<ScheduledJob | null> {
  const existing = await getJobById(jobId);
  if (!existing) return null;
  const merged: ScheduledJob = { ...existing, ...updates, lastModifiedDate: new Date().toISOString() };
  const updated = await updateGobodoRecord(jobId, jobToRecord(merged));
  return recordToJob(updated);
}

export async function deleteJob(id: string): Promise<boolean> {
  await deleteGobodoRecord(id);
  return true;
}

export async function toggleJobStatus(id: string): Promise<ScheduledJob | null> {
  const job = await getJobById(id);
  if (!job) return null;
  return updateJob(id, {
    isActive: !job.isActive,
    status: !job.isActive ? 'scheduled' : 'paused'
  });
}
